import { Injectable } from "@angular/core";
import {
	HttpClient,
	HttpHeaders,
	HttpResponse,
	HttpErrorResponse,
} from "@angular/common/http";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { NgProgress, NgProgressRef } from "@ngx-progressbar/core";
import { Observable, of, BehaviorSubject } from "rxjs";
import { map, catchError, finalize } from "rxjs/operators";
import {
	IAdapterSetting,
	ResponeTypeEnum,
	LoadingState,
	createLoadingState,
	DataBlob,
	ResponeContentType,
} from "./http.utils";
import { OAuthService } from "@royalty/oauth2";
import { RoyaltyButtonService } from "@royalty/button";
import { ErrorDialogService } from "@royalty/errordialog";
import { LocalStorageKey } from "@royalty/model";
import { ErrorConflictDialogService } from "../error-conflict";
import { CoreToolbox } from "../utils/core-toolbox";
import { EMessageLevel } from "../message/option/message-level.e";

const DOMAIN_API: string = "";

@Injectable()
export abstract class HttpService {
	public loadingState$: BehaviorSubject<LoadingState>;
	progressRef: NgProgressRef;
	constructor(
		protected router: Router,
		protected httpClient: HttpClient,
		protected toastr: ToastrService,
		protected progress: NgProgress,
		protected royaltyButtonService: RoyaltyButtonService,
		private oAuthService: OAuthService,
		private errorDialogService: ErrorDialogService,
		private errorConflictDialogService: ErrorConflictDialogService
	) {
		this.loadingState$ = new BehaviorSubject(
			createLoadingState({
				targetFunc: null,
				targetName: "",
			})
		);

		this.progressRef = progress.ref("posProgress");
	}

	protected pendingRequest: number = 0;

	protected getBaseUrl(adapterSetting?: IAdapterSetting): string {
		const version =
			adapterSetting && adapterSetting.isAuthorize
				? ""
				: "/" +
				  (adapterSetting && adapterSetting.version
						? adapterSetting.version
						: "v1");
		const controllerName =
			"/" + (adapterSetting && adapterSetting.isAdmin ? "aapi" : "api");
		return DOMAIN_API + controllerName + version + "/";
	}

	protected getDefaultHeaders(): HttpHeaders {
		return this.oAuthService.getAuthorizationHeader();
	}

	protected getSetting(adapterSetting?: IAdapterSetting): IAdapterSetting {
		let defaultSetting: IAdapterSetting = {
			data: "full",
			showResponeMessage: true,
			showProgress: true,
			responeType: ResponeTypeEnum.json,
		};
		return adapterSetting
			? { ...defaultSetting, ...adapterSetting }
			: defaultSetting;
	}

	/**
	 *
	 * @param observableRes
	 * @param setting
	 * @param targetFn name of api function
	 * @param adapterFn
	 */
	protected responseInterceptor(
		observableRes: Observable<any>,
		setting: IAdapterSetting,
		targetFn: string,
		adapterFn?: Function
	): Observable<any> {
		return observableRes.pipe(
			map((res) => this.baseAdapter(res, setting, adapterFn)),
			finalize(() => {
				this.onFinalize(setting, targetFn);
			}),
			catchError((error, source) =>
				of(this.onCatch(error, targetFn, setting))
			)
		);
	}

	private baseAdapter(
		res: HttpResponse<any>,
		setting: IAdapterSetting,
		adapterFn?: Function
	): any {
		if (res.status === 200) {
			let body = res.body;
			// TODO: Will handle again, for now just try to workarround for make sure user only need login once time
			if (body.access_token) {
				this.oAuthService.storeAccessTokenResponse(
					body.access_token,
					body.refreshToken,
					body.expiresIn,
					body.tokenType
				);
			}
			this.checkAndStoreResponseHeader(res);
			if (setting.responeType === ResponeTypeEnum.json) {
				if (setting.showResponeMessage) {
					if (setting.messageUpdateSuccess)
						body.message = setting.messageUpdateSuccess;
					if (body.message) this.toastr.success(body.message);
				}
				if (setting.data === "content") {
					return adapterFn ? adapterFn.call(undefined, body) : body;
				}
				if (setting.data === "full") {
					// modify respone content
					return adapterFn ? adapterFn.call(undefined, body) : body;
				}
			} else if (setting.responeType === ResponeTypeEnum.blob) {
				const responeContentType = res.headers.get(
					"Content-Type"
				) as ResponeContentType;
				if (responeContentType === ResponeContentType.xlsx) {
					return {
						blob: body,
						contentType: responeContentType,
						contentDisposition: res.headers.get(
							"Content-Disposition"
						),
					} as DataBlob;
				}
			}

			return body;
		}
	}

	private checkAndStoreResponseHeader(res: HttpResponse<any>): void {
		const perms = res.headers.get(LocalStorageKey.X_PERM);
		if (perms) {
			this.oAuthService.storePermissions(perms);
		}
		const defaultLanguage = res.headers.get(
			LocalStorageKey.X_DEFAULT_LANGUAGE
		);
		if (defaultLanguage) {
			this.oAuthService.storeDefaultLanguage(defaultLanguage);
		}
		const loggOffTime = res.headers.get(LocalStorageKey.X_AUTO_LOGOFF);
		if (loggOffTime) {
			this.oAuthService.storeTimeAutoLogOff(loggOffTime);
		}
	}

	private onFinalize(setting: IAdapterSetting, targetFnName: string) {
		// set time-out in case another request is call right after
		setTimeout(() => {
			if (this.pendingRequest > 0) {
				this.pendingRequest--;
			}

			this.royaltyButtonService.hide();

			if (setting.showProgress && this.pendingRequest === 0) {
				this.progressRef.complete();
			}

			this[targetFnName]["loading"] = false;
			this.loadingState$.next(
				createLoadingState({
					targetFunc: this[targetFnName],
					targetName: targetFnName,
				})
			);
		}, 0);
	}

	/**
	 * Handle error status code 400 -> 500
	 */
	private onCatch(
		response: HttpErrorResponse,
		targetFn: string,
		setting: IAdapterSetting
	) {
		let message: string =
			response.error && response.error.message
				? response.error.message
				: response.error && response.statusText
				? response.statusText
				: `Sorry, we can't process your request right now.`;
		switch (response.status) {
			case 400: {
				// CoreToolbox.messageService.present(
				// 	{ content: message },
				// 	EMessageLevel.ERROR
				// );
				this.toastr.error(message);
				break;
			}
			case 401: {
				this.openErrorDialog(response);
				break;
			}
			case 403: {
				this.toastr.error(message);
				break;
			}
			case 404: {
				this.toastr.error(message);
				break;
			}
			case 409: {
				this.openErrorConflictDialog(response);
				break;
			}
			case 500: {
				if (!setting.isReturnResponse) {
					this.toastr.error(message);
				}
				break;
			}
			default:
				this.openErrorDialog(response);
				break;
		}
		if (setting.isReturnResponse) {
			return response;
		} else {
			throw response;
		}
	}

	private openErrorDialog(response: HttpErrorResponse) {
		this.errorDialogService.openDialog(response);
	}

	private openErrorConflictDialog(response: HttpErrorResponse) {
		this.errorConflictDialogService.openDialog(response);
	}
}
